import { useEffect, useRef } from 'react'
import classNames from 'classnames'

import {
  EMPTY_OBJECT,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../../constants/common'
import { getQaSelector } from '../../../utils/common'
import { Counter } from '../../Counter'
import { FloatingTooltip } from '../../Floating'
import { useScrollSliderContext } from '../../ScrollSlider'
import { Skeleton } from '../../Skeleton'

import * as styles from './Tab.module.css'

function Tab({
  className = styles.Tab,
  count,
  disabled,
  isActive,
  isCountLoading,
  qaName,
  text,
  tooltip,
  unreadCount,
  withScrollIntoView,
  onClick,
}) {
  const { handleClickSlide } = useScrollSliderContext() ?? EMPTY_OBJECT

  const ref = useRef(null)

  useEffect(() => {
    if (withScrollIntoView && isActive) {
      // Tab widths settle asynchronously (e.g. counters arrive after the first
      // render), so re-assert visibility on every tab row resize instead of
      // scrolling once against a not-yet-final layout.
      const observer = new ResizeObserver(() => {
        handleClickSlide?.(ref.current)
      })

      observer.observe(ref.current.parentElement)

      return () => {
        observer.disconnect()
      }
    }
  }, [isActive])

  const mods = {
    [styles.active]: isActive,
    [styles.disabled]: disabled,
  }

  const tabClassName = classNames(className, mods)

  const qaProps = qaName ? getQaSelector({ value: qaName }) : null

  return (
    <FloatingTooltip
      content={tooltip}
      placement={FLOATING_ELEMENT_PLACEMENT.top}
    >
      <div
        {...qaProps}
        className={tabClassName}
        ref={ref}
        role='button'
        tabIndex={disabled ? -1 : 0}
        onClick={onClick}
      >
        <span className={styles.text}>
          {text}
          {isCountLoading ? (
            <Skeleton Element='span' className={styles.countSkeleton} />
          ) : count != null ? (
            <span className={styles.count}>{`(${count})`}</span>
          ) : null}
          {unreadCount ? (
            <Counter className={styles.count} count={unreadCount} />
          ) : null}
        </span>
      </div>
    </FloatingTooltip>
  )
}

export default Tab
